import React from 'react'
import {useIntl} from 'react-intl'

import {
  DirectoryProfile,
  isStatusField,
  profileDate,
  profileLabel,
} from './directoryProfile'

type DirectoryProfileCardProps = {
  profile: DirectoryProfile
  theme: 'light' | 'dark'
}

/** Up to two initials for the avatar circle. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')

/**
 * A person's record from a directory answer (see `parseDirectoryProfile`): the name and status
 * on top, each recorded field as a label over its value, and the empty fields named once in a
 * muted line at the bottom rather than as "not recorded" over and over.
 */
const DirectoryProfileCard: React.FC<DirectoryProfileCardProps> = ({profile, theme}) => {
  const intl = useIntl()
  const status = profile.fields.find(isStatusField)
  const fields = profile.fields.filter((field) => field !== status)

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
  const cardBg = theme === 'dark' ? 'rgba(15, 23, 42, 0.94)' : 'rgba(255,255,255,0.65)'
  const textColor = theme === 'dark' ? '#ffffff' : '#212529'
  const mutedColor = theme === 'dark' ? 'rgba(255,255,255,0.65)' : '#6c757d'
  const avatarBg = theme === 'dark' ? 'rgba(96,165,250,0.18)' : 'rgba(13,110,253,0.1)'
  const avatarColor = theme === 'dark' ? '#93c5fd' : '#0d6efd'
  const active = status ? /^active$/i.test(status.value) : false
  const statusColor = active ? (theme === 'dark' ? '#4ade80' : '#198754') : mutedColor

  const displayValue = (value: string): string => {
    const date = profileDate(value)
    return date ? intl.formatDate(date, {day: 'numeric', month: 'short', year: 'numeric'}) : value
  }

  return (
    <div
      className='attendance-v2-result chatbot-profile-card mt-2 mb-1 rounded-3 p-3'
      style={{border: `1px solid ${borderColor}`, backgroundColor: cardBg, color: textColor}}
    >
      <div className='d-flex align-items-center gap-2 mb-3'>
        <div
          className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold'
          style={{
            width: 36,
            height: 36,
            fontSize: '0.8rem',
            backgroundColor: avatarBg,
            color: avatarColor,
          }}
          aria-hidden='true'
        >
          {initialsOf(profile.name)}
        </div>
        <div className='fw-bold flex-grow-1 min-w-0' style={{fontSize: '0.95rem', color: textColor}}>
          {profile.name}
        </div>
        {status ? (
          <span
            className='attendance-v2-status-badge d-inline-flex align-items-center rounded-pill px-2 py-1'
            style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: statusColor,
              border: `1px solid ${statusColor}55`,
            }}
          >
            {status.value}
          </span>
        ) : null}
      </div>
      <dl className='chatbot-profile-card__fields mb-0'>
        {fields.map((field) => (
          <div key={field.label} className='chatbot-profile-card__field'>
            <dt style={{fontSize: '0.7rem', fontWeight: 500, color: mutedColor}}>
              {profileLabel(field.label)}
            </dt>
            <dd className='mb-0' style={{fontSize: '0.85rem', color: textColor}}>
              {displayValue(field.value)}
            </dd>
          </div>
        ))}
      </dl>
      {profile.notRecorded.length ? (
        <div
          className='mt-3 pt-2'
          style={{fontSize: '0.75rem', color: mutedColor, borderTop: `1px solid ${borderColor}`}}
        >
          {intl.formatMessage(
            {id: 'CHATBOT.PROFILE.NOT_RECORDED'},
            {fields: profile.notRecorded.join(', ')}
          )}
        </div>
      ) : null}
    </div>
  )
}

export default DirectoryProfileCard
